/**
 * TypeScript interfaces for Claims feature
 * Frontend data types with Date objects for date fields
 */

/**
 * Claim Episode event type (CLE-2166).
 * Mirrors backend Prisma enum RemitStatusType. Each child event row of an episode
 * has one of these values, classified at parse time from CLP02 + CAS + REF*F8 +
 * CLP07 lifecycle. See docs/specs/CLE-2166-claim-episodes.md §5.
 */
export enum RemitStatusType {
  PRIMARY = "PRIMARY",
  REPROCESS = "REPROCESS",
  REVERSAL = "REVERSAL",
  DUPLICATE_DENIAL = "DUPLICATE_DENIAL",
  PRIMARY_SPLIT = "PRIMARY_SPLIT",
  DENIAL = "DENIAL",
  SECONDARY = "SECONDARY",
}

export const REMIT_STATUS_LABELS: Record<RemitStatusType, string> = {
  [RemitStatusType.PRIMARY]: "Primary",
  [RemitStatusType.REPROCESS]: "Reprocess",
  [RemitStatusType.REVERSAL]: "Reversal",
  [RemitStatusType.DUPLICATE_DENIAL]: "Duplicate Denial",
  [RemitStatusType.PRIMARY_SPLIT]: "Bilateral Companion",
  [RemitStatusType.DENIAL]: "Denial",
  [RemitStatusType.SECONDARY]: "Secondary",
};

export const REMIT_STATUS_COLORS: Record<
  RemitStatusType,
  "info" | "success" | "warning" | "danger" | undefined
> = {
  [RemitStatusType.PRIMARY]: "success",
  [RemitStatusType.REPROCESS]: "info",
  [RemitStatusType.REVERSAL]: "warning",
  [RemitStatusType.DUPLICATE_DENIAL]: undefined,
  [RemitStatusType.PRIMARY_SPLIT]: "info",
  [RemitStatusType.DENIAL]: "danger",
  [RemitStatusType.SECONDARY]: undefined,
};

/**
 * Resolution Status enum
 * Matches backend Prisma enum ResolutionStatus
 */
export enum ResolutionStatus {
  IN_REVIEW = "IN_REVIEW",
  SUBMITTED = "SUBMITTED",
  CLOSED = "CLOSED",
  NO_APPEAL_NEEDED = "NO_APPEAL_NEEDED",
}

/**
 * Resolution history entry
 * Stored in resolutionHistory JSONB array
 */
export interface IResolutionHistoryEntry {
  status: ResolutionStatus;
  notes?: string;
  userId: string;
  userName: string;
  timestamp: string; // ISO 8601
}

/**
 * Resolution status labels for display
 */
export const RESOLUTION_STATUS_LABELS: Record<
  ResolutionStatus | "null",
  string
> = {
  [ResolutionStatus.IN_REVIEW]: "In Review",
  [ResolutionStatus.SUBMITTED]: "Submitted",
  [ResolutionStatus.CLOSED]: "Closed",
  [ResolutionStatus.NO_APPEAL_NEEDED]: "No Appeal Needed",
  null: "Not Reviewed",
};

/**
 * Resolution status colors (PrimeReact Tag severities)
 * Note: PrimeReact Tag only supports 'success' | 'info' | 'warning' | 'danger' | undefined
 */
export const RESOLUTION_STATUS_COLORS: Record<
  ResolutionStatus | "null",
  "danger" | "warning" | "info" | "success" | undefined
> = {
  null: "danger", // Red - Not Reviewed
  [ResolutionStatus.IN_REVIEW]: "warning", // Orange - In Review
  [ResolutionStatus.SUBMITTED]: "info", // Blue - Submitted
  [ResolutionStatus.CLOSED]: "success", // Green - Closed
  [ResolutionStatus.NO_APPEAL_NEEDED]: undefined, // Default gray - No Appeal Needed
};

/**
 * Filter options for resolution status dropdown
 */
export const RESOLUTION_FILTER_OPTIONS = [
  { label: "Not Reviewed", value: "not_reviewed" },
  { label: "In Review", value: "in_review" },
  { label: "All", value: "all" },
] as const;

/**
 * Payment type enum for filtering claims
 * Note: "underpayment" as a business concept remains - this represents claims where payer paid less than expected
 */
export type PaymentType =
  | "all"
  | "underpayment"
  | "overpayment"
  | "correct"
  | "zero_paid";

// Unified filter options - includes both payment types and categories
// Values starting with CATEGORY_ are sent to categories parameter
// Other values are sent to paymentTypes parameter
export const PAYMENT_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "All Payment Types", value: "all" },
  { label: "Underpayments", value: "underpayment" },
  { label: "Overpayments", value: "overpayment" },
  { label: "Correct Payments", value: "correct" },
  { label: "Zero-Paid Claims", value: "zero_paid" },
  {
    label: "Charges Below Contract Rate",
    value: "CATEGORY_CHARGE_BELOW_CONTRACT_RATE",
  },
];

/**
 * Denial status (CLE-2225) — orthogonal to PaymentType. A claim can be both an
 * underpayment and a partial denial. PARTIAL_DENIAL = some lines denied but the
 * payer paid something; FULL_DENIAL = every payable line denied ($0 paid).
 */
export type DenialStatus = "NONE" | "PARTIAL_DENIAL" | "FULL_DENIAL";

export const DENIAL_STATUS_LABELS: Record<DenialStatus, string> = {
  NONE: "No Denial",
  PARTIAL_DENIAL: "Partial Denial",
  FULL_DENIAL: "Full Denial",
};

// Filter options for the claims-list Denial Status filter. Lowercase values match
// the API (denialStatuses query param).
export const DENIAL_STATUS_FILTER_OPTIONS: { label: string; value: string }[] =
  [
    { label: "Partial Denial", value: "partial_denial" },
    { label: "Full Denial", value: "full_denial" },
    { label: "No Denial", value: "none" },
  ];

export interface IMissingCode {
  code: string;
  quantity: number;
}

export interface ILineItemAdjustment {
  group: string;
  code: string;
  amount: number;
}

export interface ILineItemReference {
  qualifier: string;
  value: string;
}

export interface ILineItemRemark {
  qualifier: string;
  code: string;
}

export interface ILineItem {
  cptCode: string;
  lineProcedureCode: string | null;
  modifier: string | null;
  revenueCode: string | null;
  lineControlNumber: string | null;
  quantity: number;
  billedUnits: number;
  lineCharge: number;
  lineAllowed: number;
  linePaid: number;
  lineTotalAdjAmount: number;
  patientResponsibility?: number; // Sum of all PR adjustments for this line
  adjustments: ILineItemAdjustment[];
  references: ILineItemReference[];
  remarks: ILineItemRemark[];
}

/**
 * Payer type enum matching Prisma schema
 */
export type PayerType =
  | "UhcMedicareAdvantage"
  | "OriginalMedicare"
  | "Commercial";

/**
 * Matched Order (estimate) data from claims-to-estimates matching
 */
export interface IMatchedOrder {
  id: string;
  dateOfService: string; // ISO 8601
  amountInCents: number;
  serviceDetails: any;
  benefitDetails: any;
  patient: {
    firstName: string;
    lastName: string;
  };
  payer?: {
    type: PayerType | null;
    name?: string;
  } | null;
  gfeQuote?: {
    remainingDeductibleInCents: number | null;
    outOfPocketMaxInCents: number | null;
    flatCopayInCents: number | null;
    coinsurancePercent: number | null;
    pricingBreakdown: any;
  } | null;
}

export interface IEdi837ServiceLine {
  code: string;
  revenueCode?: string;
  modifier?: string;
  quantity: number;
  chargeAmount?: number;
}

export interface IMatched837Claim {
  claimNumber: string;
  chargeAmount: number;
  typeOfBill: string | null;
  placeOfService: string | null;
  patientClass: "Inpatient" | "Outpatient" | "Unknown";
  payerClaimControlNumber: string | null;
  patientName: string | null;
  serviceDate: string | null;
  providerNPI: string | null;
  drgCode: string | null;
  caseSignature: string | null;
  frequencyCode: string;
  isAuthoritative: boolean;
  serviceLines: IEdi837ServiceLine[];
}

// Inpatient Outlier — composable trigger/formula/mode rule engine (CLE-2120 v3).
// Mirrors the services-side shapes in inpatient-outlier.util.ts so the SPA can
// render the audit trail on the Expected Amounts tab.
//
// As of CLE-2120 follow-up (services PR #2942), the stored trigger and formula
// objects include BOTH the runtime evaluation result AND the original rule
// config (operator, days, caseRateInCents, includedDays, losAdjustment, etc.).
// All config fields are marked optional here so older stored rows still parse.
export interface IInpatientOutlierTriggerResult {
  type: string;
  fired: boolean;
  // Runtime evaluation result
  resolvedThresholdInCents?: number;
  effectiveChargesInCents?: number;
  actualLos?: number | null;
  resolvedDays?: number | null;
  matchedDischargeStatus?: string | null;
  matchedCode?: string | null;
  notes?: string;
  // Original rule config (added by services PR #2942 / CLE-2120 follow-up).
  // STANDARD/MODIFIED CHARGE_THRESHOLD:
  thresholdMethod?: "FIXED" | "MULTIPLIER" | "BASE_PLUS_FIXED";
  baseReference?: "BASE_PAYMENT" | "RAW_BASE_PAYMENT";
  fixedAmountInCents?: number;
  multiplier?: number;
  chargeFloorInCents?: number;
  costToChargeRatio?: number; // MODIFIED_CHARGE_THRESHOLD only
  // LOS_RANGE:
  operator?: "GT" | "GTE" | "LT" | "LTE" | "EQ";
  days?: number;
  daysSource?: "MANUAL" | "FEE_SCHEDULE";
  // PER_CODE_CHARGE_THRESHOLD:
  codeChargeFloorInCents?: number;
  onlyCodesWithoutFeeSchedule?: boolean;
  // DISCHARGE_STATUS:
  codes?: string[];
}

export interface IInpatientOutlierFormulaResult {
  type: string;
  calculatedAmountInCents: number | null;
  // Runtime calculation result
  perDiemInCents?: number;
  excessDays?: number;
  effectiveChargesInCents?: number;
  thresholdInCents?: number;
  flatRateInCents?: number;
  percent?: number;
  reason?: string;
  // Original rule config (added by services PR #2942 / CLE-2120 follow-up).
  // PER_DIEM_X_LOS / PER_DIEM_X_EXCESS_LOS:
  perDiemSource?:
    | "MANUAL"
    | "CASE_RATE_DIV_ALOS_TIMES_MULTIPLIER"
    | "CASE_RATE_DIV_ACTUAL_LOS"
    | "CASE_RATE_DIV_ALOS";
  manualPerDiemInCents?: number;
  losAdjustment?: "NONE" | "PLUS_1" | "MINUS_1";
  capAtDrgRate?: boolean;
  excessLosBase?: number;
  excessLosBaseSource?: "MANUAL" | "FEE_SCHEDULE";
  // CASE_RATE_PLUS_PER_DIEM:
  caseRateInCents?: number;
  includedDays?: number;
}

export interface IInpatientOutlierPerClaimRule {
  inpatientRulesetId: string;
  rulesetName: string;
  mode: "ADDITIVE" | "MAX" | "REPLACE";
  fired: boolean;
  trigger: IInpatientOutlierTriggerResult;
  formula: IInpatientOutlierFormulaResult;
}

export interface IInpatientOutlierPerCodeRule {
  inpatientRulesetId: string;
  rulesetName: string;
  mode: "ADDITIVE" | "MAX" | "REPLACE";
  fired: boolean;
  // Per-code rule trigger carries the trigger's config alongside its type. The runtime
  // evaluation happens per-line (in `lines`), so the trigger object itself only holds
  // type + config — no claim-level fired/effective values.
  trigger: {
    type: "PER_CODE_CHARGE_THRESHOLD";
    codeChargeFloorInCents?: number;
    onlyCodesWithoutFeeSchedule?: boolean;
  };
  // Formula carries config used by per-line calculation (e.g. percent for
  // PERCENT_OF_CHARGES, flatRateInCents for FLAT_RATE).
  formula:
    | IInpatientOutlierFormulaResult
    | { type: string; [key: string]: unknown };
  totalCalculatedAmountInCents: number;
  lines: {
    code: string;
    chargeAmountInCents: number;
    fired: boolean;
    calculatedAmountInCents: number | null;
    reason?: string;
  }[];
}

export type IInpatientOutlierRule =
  | IInpatientOutlierPerClaimRule
  | IInpatientOutlierPerCodeRule;

export interface IInpatientOutlierCalculation {
  rules: IInpatientOutlierRule[];
  aggregateAdditiveInCents: number | null;
  aggregateMaxInCents: number | null;
  replaceInCents: number | null;
}

// Lesser-of-charges cap — runs after inpatient rulesets to cap the final expected
// at billed charges × multiplier. Scope decides whether the cap is evaluated against
// the claim's total charges or per service line.
export type LesserOfChargesScope = "LINE_CHARGES" | "TOTAL_CHARGES";

// CAP: runs on top of a fee-schedule expected, lowering it when charges × multiplier
// is the smaller number. PRIMARY: no fee schedule produced an expected, so the rule
// IS the pricing (expected = multiplier × billed charges). PRIMARY claims carry a
// null `originalExpectedInCents` because there is nothing to compare against.
export type LesserOfChargesMode = "CAP" | "PRIMARY";

// One entry per line the cap actually changed (fired lines only — the backend
// omits non-targeted and unchanged lines).
export interface ILesserOfChargesPerLineCap {
  cptCode: string;
  cappedExpectedInCents: number;
}

export interface ILesserOfChargesCalculation {
  appliedRulesetId: string;
  ruleName: string | null;
  scope: LesserOfChargesScope;
  multiplier: number;
  mode: LesserOfChargesMode;
  fired: boolean;
  // Null in PRIMARY mode (no fee-schedule expected existed to compare against).
  originalExpectedInCents: number | null;
  cappedExpectedInCents: number;
  totalChargeInCents: number;
  // TOTAL_CHARGES only — sum of charges from lines whose CPT participates in
  // the rule (in `codes` and not in `excludedCodes`). The cap denominator.
  includedChargeInCents?: number;
  capInCents?: number;
  perLineCaps?: ILesserOfChargesPerLineCap[];
}

export interface IClaim {
  id: string;
  patientName: string;
  patientDOB: Date | null;
  patientAccountNumber: string | null;
  claimNumber: string | null;
  claimPayerId: string | null;
  claimPayerName: string;
  claimPayerGroupNumber: string | null;
  claimNetworkStatus: string | null;
  clearPayerName: string | null;
  clearParentPayerName: string | null;
  payerId: number | null; // Numeric payer ID for fetching allowables
  serviceDate: Date;
  chargeAmount: number | null; // Display in dollars (divide by 100)
  expectedAmount: number | null; // Display in dollars (divide by 100) — already reduced when CLE-2295 sequestration applies; the pre-sequestration value is expectedAmount + sequestrationDiscountInCents
  claimAmount: number; // Display in dollars (divide by 100)
  totalPaid: number | null; // Insurance-paid-only amount in cents (divide by 100); $0 = denial
  amountDifference: number | null; // Display in dollars (divide by 100)
  // CLE-2295: Medicare/MA sequestration discount applied to expectedAmount in cents.
  // Null when the rule didn't apply (account flag off, no CO-253, or pre-CLE-2295 row).
  sequestrationDiscountInCents?: number | null;
  caseSignature: string;
  paymentType?: "UNDERPAYMENT" | "OVERPAYMENT" | "CORRECT" | "UNKNOWN" | null;
  denialStatus?: DenialStatus | null; // CLE-2225: denial classification, orthogonal to paymentType
  // CLE-2225: the denial classification (code/title/reason) that produced denialStatus.
  // Null for non-denials; reason is surfaced as a hover tooltip in the claim modal.
  denialClassificationCode?: string | null;
  denialClassificationTitle?: string | null;
  denialClassificationReason?: string | null;
  lineItems?: ILineItem[]; // Line-level details from EDI file
  category: string | null; // Categorizes type of payment discrepancy
  patientClass?: "INPATIENT" | "OUTPATIENT" | "UNKNOWN" | null;
  // Raw CLP08 from 835 — a Type-of-Bill prefix on institutional claims, a CMS Place-of-Service
  // code on professional claims. Interpret via claimFormType (e.g., "11", "13", "83", "24").
  facilityTypeCode?: string | null;
  // INSTITUTIONAL (UB-04 facility claim) vs PROFESSIONAL (CMS-1500) — CLE-2235
  claimFormType?: "INSTITUTIONAL" | "PROFESSIONAL" | "UNKNOWN" | null;
  drgCode?: string | null; // CLP11 from 835 - DRG code for inpatient claims (e.g., "807")
  typeOfBill?: string | null; // 3-digit TOB — from 837, or reconstructed as CLP08‖CLP09 (e.g., "131", "111")
  aiAnalysisResult?: IAIAnalysisResult | null; // AI analysis of claim
  differenceAnalysis?: IDifferenceAnalysisResult | null; // AI difference analysis vs similar claims
  promptVersion?: IPromptVersionSummary; // Prompt version used for AI analysis
  resolutionStatus: ResolutionStatus | null; // Current resolution status
  resolutionHistory?: IResolutionHistoryEntry[]; // Audit trail of status changes
  ediConversionFile?: {
    fileName: string;
    displayName: string;
    createdAt: Date;
  };
  precalculatedAllowable?: {
    id: string;
    calculatedData: any;
    allowableAccuracyStatus: boolean;
    missingCodes: IMissingCode[];
  };
  // 837 Claim Submission Data (Feature CLE-1878)
  matched837Claims?: IMatched837Claim[] | null;
  edi837CaseSignature?: string | null;
  edi837ExpectedAmount?: number | null;
  // DRG LOS/Transfer Pricing (CLE-1885, CLE-1904)
  dischargeStatus?: string | null;
  losOutlierCalculation?: {
    pricingRule: string;
    ruleApplied: boolean;
    ruleStatus: string;
    drgBaseRateInCents: number;
    alos: number | null;
    los: number | null;
    losSource: string | null;
    // LOS outlier fields
    threshold?: number | null;
    multiplier?: number;
    outlierDays?: number;
    perDiemInCents?: number;
    outlierAddOnInCents?: number;
    expectedAllowedInCents?: number;
    // Transfer fields
    dischargeStatus?: string;
    transferPaymentInCents?: number;
    // DRG code used for pricing
    drgCode?: string;
  } | null;
  // High-Cost Outlier (CLE-1886, CLE-1903)
  isHighCostOutlier?: boolean | null;
  outlierPaymentInCents?: number | null;
  outlierCalculation?: {
    inpatientRulesetId: string;
    type: string;
    costToChargeRatio: number;
    fixedLossThresholdInCents: number;
    outlierPercent: number;
    estimatedCostInCents: number;
    triggerAmountInCents: number;
  } | null;
  // Stop Loss (CLE-1897)
  isStopLoss?: boolean | null;
  stopLossPaymentInCents?: number | null;
  stopLossCalculation?: {
    inpatientRulesetId: string;
    type: string;
    thresholdMultiplier: number;
    paymentPercent: number;
    thresholdAmountInCents: number;
    chargeAmountInCents: number;
  } | null;
  // Charge Outlier (CLE-2120)
  isChargeOutlier?: boolean | null;
  chargeOutlierPaymentInCents?: number | null;
  chargeOutlierCalculation?: {
    inpatientRulesetId: string;
    type: string;
    thresholdType: string;
    thresholdAmountInCents: number;
    outlierPercent: number;
    chargeFloorInCents?: number | null;
    chargeAmountInCents: number;
    edi837RawBaseExpected: number;
    excessChargesInCents?: number;
    chargeOutlierPaymentInCents?: number;
  } | null;
  // Inpatient Outlier — composable trigger/formula/mode rule engine (CLE-2120 v3)
  isInpatientOutlier?: boolean | null;
  inpatientOutlierPaymentInCents?: number | null;
  inpatientOutlierAdditiveInCents?: number | null;
  inpatientOutlierMaxInCents?: number | null;
  inpatientOutlierReplaceInCents?: number | null;
  inpatientOutlierCalculation?: IInpatientOutlierCalculation | null;
  // Lesser-of-charges cap (post-pricing) — caps expected at billed charges × multiplier
  lesserOfChargesCalculation?: ILesserOfChargesCalculation | null;
  passThroughCalculation?: {
    type: string;
    totalPassThroughAmountInCents: number;
    codes: {
      code: string;
      inpatientRulesetId: string;
      chargeMultiplier: number;
      thresholdInCents: number;
      chargeInCents: number;
      calculatedAmountInCents: number;
    }[];
  } | null;
  edi837PrecalculatedAllowable?: {
    id: string;
    calculatedData: any;
    allowableAccuracyStatus: boolean;
    missingCodes: IMissingCode[];
  } | null;
  // Order matching - linked estimate
  orderId?: string | null;
  orderMatchConfidence?: number | null; // 0.0-1.0
  orderMatchedAt?: string | null; // ISO 8601
  order?: IMatchedOrder | null;
  // Variance analysis - root causes for claim vs estimate discrepancies
  varianceAnalysis?: IVarianceAnalysis | null;
  // Claim-level summary data for inpatient claims (no SVC lines)
  claimSummaryData?: {
    adjustments?: Array<{ group: string; code: string; amount: number }>;
    coveredAmount?: number | null;
    drgWeight?: string | null;
    drgAmount?: number | null;
    coveredDays?: number | null;
    lengthOfStay?: number | null;
  } | null;
  // NCCI Bypass - per-claim toggle for NCCI edit bypass
  ncciBypassEnabled?: boolean;
  // Manual Payer Override - indicates payer was manually set by user
  isPayerManuallySet?: boolean;
  // CLE-2166 Episode model
  isEpisodeRoot?: boolean;
  eventCount?: number | null; // Populated on episode rows; child count
  childEvents?: IClaimEvent[]; // Present on getClaimById response for episode rows
  episodeNetLineItems?: ILineItem[] | null; // Cached post-aggregation lines for the Expected Amounts tab
  patientIdentityHash?: string | null;
}

/**
 * One claim event under an episode (CLE-2166). Returned as `childEvents` on the
 * episode-detail response. Each event row corresponds to one CLP segment from a
 * source 835 file.
 */
export interface IClaimEvent {
  id: string;
  remitStatusType: RemitStatusType | null;
  remitStatusCode: string | null;
  payerClaimControlNumber: string | null;
  bprPaymentDate: string | null; // ISO 8601 date
  supersedesPayerClaimControlNumber: string | null;
  supersedesClaimId: string | null;
  caseSignature: string;
  chargeAmount: number | null;
  claimAmount: number;
  totalPaid: number | null;
  lineItems: ILineItem[] | null;
  claimSummaryData: IClaim["claimSummaryData"];
  ediConversionFile?: {
    fileName: string;
    displayName: string;
    createdAt: Date;
  } | null;
}

/**
 * AI Analysis Result interfaces
 */
export interface IAIAnalysisResult {
  analyzedAt: string; // ISO 8601 timestamp
  model: string; // OpenAI model used
  summary: string; // High-level summary of the claim analysis
  effectiveTotals: IAIEffectiveTotals;
  excludedImplants: IAIExcludedImplant[];
  lineItemDiscrepancies: IAILineItemDiscrepancy[];
  classifications: IAIClassification[];
  resolution: IAIResolution;
  variance: IVarianceCalculation;
  action?: string | null; // Recommended action: "appeal", "reprocess", "reprocess with correct modifier", "no appeal", etc.
}

export interface IAIEffectiveTotals {
  originalExpectedAllowed: number; // cents
  originalActualAllowed: number; // cents
  excludedImplantExpected: number; // cents
  excludedImplantActual: number; // cents
  effectiveExpectedAllowed: number; // cents
  effectiveActualAllowed: number; // cents
  underpaymentAmount: number; // cents - business concept: how much payer underpaid
}

export interface IAIExcludedImplant {
  cpt: string;
  modifier: string | null;
  revenueCode: string;
  expectedAllowed: number; // cents
  actualAllowed: number; // cents
  reason: string;
}

export interface IAILineItemDiscrepancy {
  cpt: string;
  modifier: string | null;
  expectedAllowed: number; // cents
  actualAllowed: number; // cents
  adjustmentCodes: string[];
  explanation: string;
}

export interface IAIClassification {
  code: string; // e.g., "C5", "D2"
  category: string; // e.g., "Contract / Calculation Errors"
  title: string; // e.g., "MPPR Error"
  confidence: "High" | "Medium" | "Low";
  explanation: string;
}

export interface IAIResolution {
  hasRealDiscrepancy: boolean;
  appealLetterDraft: string;
}

export interface IVarianceCalculation {
  expectedAllowed: number; // cents
  actualAllowed: number; // cents
  variance: number; // cents
  percentDifference: number; // percentage
}

export interface IClaimFilters {
  accountId: string; // Required
  search?: string;
  startDate?: Date;
  endDate?: Date;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  pageSize?: number;
  minDifferencePercent?: number; // Minimum difference percentage filter
  chargeBelowContractRateFilter?: "include" | "exclude" | "only";
  categories?: string[]; // Array of category values to filter by (e.g., 'CHARGE_BELOW_CONTRACT_RATE')
  underpaymentOnly?: boolean; // When true, only show records where amountDifference < 0 (true underpayments) - DEPRECATED: use paymentTypes instead
  paymentTypes?: string[]; // Array of payment types to filter by (underpayment, overpayment, correct, zero_paid, all)
  denialStatuses?: string[]; // CLE-2225: denial statuses to filter by (none, partial_denial, full_denial); orthogonal to paymentTypes
  resolutionFilter?: "not_reviewed" | "in_review" | "all"; // Filter by resolution status
  classificationFilter?: string; // AI classification code filter (e.g., "C5", "D2") or "not_analyzed" or "all"
  payerIdFilter?: string; // Filter by Clear payer id (numeric id as string) or 'no-match'
  patientClassFilter?: "inpatient" | "outpatient" | "all"; // Filter by patient class
  claimCategoryFilter?:
    | "institutional"
    | "professional-fac"
    | "professional-nonfac"
    | "anesthesia"
    | "all"; // CLE-2236 follow-up: filter by claim form type + place of service
  hasMatchedOrder?: boolean; // When true, only show claims with a matched order (estimate insights)
  minEventCount?: number; // CLE-2166: filter episodes by minimum child event count
  maxEventCount?: number;
}

export interface IClaimResponse {
  data: IClaim[];
  totalCount: number;
  page: number;
  pageSize: number;
  message?: string;
}

// Category constants (match backend enum values)
export const CLAIM_CATEGORIES = {
  CHARGE_BELOW_CONTRACT_RATE: "CHARGE_BELOW_CONTRACT_RATE",
  CHARGE_BELOW_CONTRACT_RATE_LINE: "CHARGE_BELOW_CONTRACT_RATE_LINE",
  CHARGE_BELOW_CONTRACT_RATE_TOTAL: "CHARGE_BELOW_CONTRACT_RATE_TOTAL",
  CHARGE_BASED_PRICING_LINE: "CHARGE_BASED_PRICING_LINE",
  CHARGE_BASED_PRICING_TOTAL: "CHARGE_BASED_PRICING_TOTAL",
} as const;

/** Every category that signals the claim was capped by the lesser-of-charges rule
 *  (or its legacy predecessor). Use this when filtering or rendering "below
 *  contract rate" claims so the legacy and new enum values stay grouped. */
export const CHARGE_BELOW_CONTRACT_RATE_CATEGORIES: string[] = [
  CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE,
  CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_LINE,
  CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_TOTAL,
];

/** Categories emitted when a wildcard lesser-of-charges rule is the sole pricing
 *  source (no fee schedule produced an expected). Informational — these claims
 *  aren't variance flags, they're just how the contract prices. */
export const CHARGE_BASED_PRICING_CATEGORIES: string[] = [
  CLAIM_CATEGORIES.CHARGE_BASED_PRICING_LINE,
  CLAIM_CATEGORIES.CHARGE_BASED_PRICING_TOTAL,
];

export const CATEGORY_LABELS: Record<string, string> = {
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE]: "Below Contract",
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_LINE]: "Below Contract (line)",
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_TOTAL]: "Below Contract (total)",
  [CLAIM_CATEGORIES.CHARGE_BASED_PRICING_LINE]: "Charge-Based Pricing",
  [CLAIM_CATEGORIES.CHARGE_BASED_PRICING_TOTAL]: "Charge-Based Pricing",
};

export const CATEGORY_COLORS: Record<
  string,
  "info" | "success" | "warning" | "danger" | undefined
> = {
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE]: "info",
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_LINE]: "info",
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE_TOTAL]: "info",
  // Distinct from "info" (used for the variance-flavored Below-Contract badges):
  // undefined renders as PrimeReact's default neutral gray, making it clear that
  // charge-based pricing is just how the contract prices, not a problem to review.
  [CLAIM_CATEGORIES.CHARGE_BASED_PRICING_LINE]: undefined,
  [CLAIM_CATEGORIES.CHARGE_BASED_PRICING_TOTAL]: undefined,
};

/**
 * Underpayment Analysis Prompt interfaces
 * Versioned AI prompts for claim analysis
 * Note: Name kept as "UnderpaymentAnalysisPrompt" to match unchanged backend table name
 */

export interface IUnderpaymentAnalysisPrompt {
  id: string; // UUID
  version: number; // Sequential version number
  createdAt: string; // ISO 8601 timestamp
  identity: string; // First section: Role/identity description
  issueSummary: string; // Second section: STEP 1-2, implant exclusion, discrepancy detection
  classification: string; // Third section: STEP 3, taxonomy, deterministic rules
  appealLetter: string; // Fourth section: Appeal letter template (JSON schema injected automatically)
  reprocessLetter: string; // Fifth section: Reprocess letter template (optional, may be empty)
  finalRules: string; // Sixth section: Final rules and closing instructions
}

export interface IPromptVersionSummary {
  id: string; // UUID
  version: number; // Sequential version number
  createdAt: string; // ISO 8601 timestamp
}

/**
 * Variance Analysis interfaces
 * Root cause analysis for discrepancies between 835 claim data and matched estimates
 */

export interface IVarianceAnalysis {
  /** True if any root cause discrepancy was detected */
  hasDiscrepancy: boolean;
  /** Detailed root cause analysis */
  rootCauses: IRootCauses;
}

export interface IRootCauses {
  /** Allowable amount mismatch - claim vs expected (underpayment/overpayment) */
  allowableMismatch: IAllowableMismatchAnalysis | null;
  /** Case signature change between claim and estimate */
  codeChange: ICodeChangeAnalysis | null;
  /** Implant CPT codes present in one but not the other */
  implantPtResponsibility: IImplantPtResponsibilityAnalysis | null;
  /** Benefit component mismatches (deductible, coinsurance %, OOP, copay) */
  benefitsMismatch: IBenefitsMismatchAnalysis | null;
  /** Payer type mismatch between claim (MA) and estimate (Commercial), or vice versa */
  payerTypeMismatch: IPayerTypeMismatchAnalysis | null;
}

export interface IAllowableMismatchAnalysis {
  /** Whether a mismatch was detected */
  detected: boolean;
  /** Type of mismatch: claim_high (claim > expected, overpayment), claim_low (claim < expected, underpayment), or null */
  type: "claim_high" | "claim_low" | null;
  /** Expected allowed amount in cents (from precalculated allowables) */
  expectedAllowed: number;
  /** Claim allowed amount in cents (actual payer payment from 835) */
  claimAllowed: number;
  /** Variance in cents (claim - expected), positive = claim higher than expected */
  variance: number;
}

export interface IQuantityChange {
  /** CPT code */
  code: string;
  /** Quantity in claim */
  claimQty: number;
  /** Quantity in estimate */
  estimateQty: number;
}

export interface ICodeChangeAnalysis {
  /** Whether a code change was detected */
  detected: boolean;
  /** Claim case signature (CPT:qty format) */
  claimSignature: string;
  /** Estimate case signature (CPT:qty format) */
  estimateSignature: string;
  /** CPT codes in claim but not in estimate */
  codesAdded: string[];
  /** CPT codes in estimate but not in claim */
  codesDropped: string[];
  /** CPT codes in both but with different quantities */
  quantityChanges: IQuantityChange[];
}

export interface IImplantPtResponsibilityAnalysis {
  /** Whether an implant discrepancy was detected */
  detected: boolean;
  /** Implant CPT codes in claim but not in estimate */
  implantsInClaimOnly: string[];
  /** Implant CPT codes in estimate but not in claim */
  implantsInEstimateOnly: string[];
}

export interface IBenefitsMismatchAnalysis {
  /** Whether any benefit mismatch was detected */
  detected: boolean;
  /** Deductible mismatch (claim > estimate only) */
  deductible: IBenefitMismatchDetail | null;
  /** Coinsurance percentage mismatch (any direction) */
  coinsurancePercent: ICoinsurancePercentMismatch | null;
  /** OOP max mismatch (claim > estimate only) */
  oopMax: IBenefitMismatchDetail | null;
  /** Copay mismatch (claim > estimate only) */
  copay: IBenefitMismatchDetail | null;
}

export interface IBenefitMismatchDetail {
  /** Whether this specific benefit mismatch was detected */
  detected: boolean;
  /** Claim amount in cents */
  claimAmount: number;
  /** Estimate amount in cents */
  estimateAmount: number;
  /** Variance in cents */
  variance: number;
}

export interface ICoinsurancePercentMismatch {
  /** Whether a coinsurance percentage mismatch was detected */
  detected: boolean;
  /** Claim coinsurance percentage */
  claimPercent: number;
  /** Estimate coinsurance percentage */
  estimatePercent: number;
  /** Variance in percentage points */
  variance: number;
}

export interface IPayerTypeMismatchAnalysis {
  /** Whether a payer type mismatch was detected */
  detected: boolean;
  /** Claim payer type */
  claimPayerType: PayerType | null;
  /** Estimate payer type */
  estimatePayerType: PayerType | null;
  /** Type of mismatch: claim is MA and estimate is Commercial, or vice versa */
  mismatchType:
    | "claim_ma_estimate_commercial"
    | "claim_commercial_estimate_ma"
    | null;
}

/**
 * AI Difference Analysis Result
 * Compares an underpaid claim against similar correctly-paid claims
 */
export interface IDifferenceAnalysisResult {
  /** When analysis was performed (ISO 8601 timestamp) */
  analyzedAt: string;
  /** Plain text analysis explaining why this claim was underpaid */
  analysis: string;
  /** IDs of the similar claims used in the comparison */
  similarClaimIds: string[];
  /** OpenAI model used */
  model: string;
}

/**
 * Similar Claims Search interfaces
 * Used for finding correctly-paid claims as evidence for appeals
 */

/** Summary of a similar correctly-paid claim */
export interface ISimilarClaimSummary {
  /** Unique identifier (UUID) */
  id: string;
  /** Claim number from 835 file */
  claimNumber: string | null;
  /** Date of service (ISO 8601 format) */
  serviceDate: string;
  /** Patient full name */
  patientName: string;
  /** Case signature (CPT:qty format, e.g., "19318:2,64445:1") */
  caseSignature: string;
  /** Actual allowed amount from 835 in cents */
  claimAmount: number;
  /** Expected allowed amount per contract in cents */
  expectedAmount: number | null;
  /** Match percentage (0-100) based on overlapping problem CPT codes */
  matchPercentage: number;
  /** Match tier: 'perfect' for 100%, 'partial' for 50-99% */
  matchTier: "perfect" | "partial";
}

/** Response from similar claims search endpoint */
export interface ISimilarClaimsResponse {
  /** Array of similar correctly-paid claims */
  similarClaims: ISimilarClaimSummary[];
  /** Criteria used to find similar claims */
  matchCriteria: {
    /** CPT codes with underpayment from AI analysis (where actualAllowed < expectedAllowed) */
    problemCptCodes: string[];
    /** Payer name used for matching */
    clearPayerName: string;
  };
  /** Total number of similar claims found */
  totalFound: number;
}
